import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, apiClient } from '../../lib/api';
import { Button } from '../../components/Button';
import { PageStage } from '../../components/PageStage';
import { SectionHead } from '../../components/SectionHead';
import { toast } from '../../lib/toast';
import styles from './Coach.module.css';

interface TranscribeResponse {
  text: string;
  language?: string | null;
}

interface FillerCount {
  word: string;
  count: number;
}
interface SpeechAnalysisResponse {
  duration_sec: number;
  word_count: number;
  wpm: number;
  fillers: FillerCount[];
  filler_total: number;
  filler_pct: number;
  hesitations_count: number;
  avg_sentence_length: number;
  max_sentence_length: number;
  commentary: string;
}

// Backend catalog shape (from /api/v1/coach/drills).
interface DrillCategory {
  skill: string;
  name: string;
  description: string;
  difficulty_range: [number, number];
  populated: boolean;
}

// Backend single-drill shape (from /api/v1/coach/drills/{skill}).
interface DrillDetail extends DrillCategory {
  prompt_templates?: Array<{
    difficulty: number;
    duration_seconds?: number;
    prompts: string[];
  }>;
  success_criteria?: string[];
}

type View = 'picker' | 'active';

export function CoachPage() {
  const [categories, setCategories] = useState<DrillCategory[]>([]);
  const [view, setView] = useState<View>('picker');
  const [active, setActive] = useState<DrillDetail | null>(null);
  const [activePrompt, setActivePrompt] = useState<string>('');
  const [response, setResponse] = useState('');
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [speechMetrics, setSpeechMetrics] = useState<SpeechAnalysisResponse | null>(null);
  const lastBlobRef = useRef<{ blob: Blob; ext: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get<DrillCategory[] | { items: DrillCategory[] }>(
          '/api/v1/coach/drills',
        );
        const list = Array.isArray(res) ? res : (res?.items ?? []);
        setCategories(list);
      } catch (err) {
        if (err instanceof ApiError && err.status >= 500) {
          toast.error('Не удалось загрузить дриллы.');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function pick(cat: DrillCategory) {
    if (!cat.populated) {
      toast.info('Эта категория ещё не наполнена. Vдумайте сами или выберите другую.');
      return;
    }
    setSubmitted(null);
    setResponse('');
    setSpeechMetrics(null);
    lastBlobRef.current = null;
    try {
      const detail = await apiClient.get<DrillDetail>(
        `/api/v1/coach/drills/${encodeURIComponent(cat.skill)}`,
      );
      setActive(detail);
      // Pick a random prompt from the easiest tier available.
      const promptList = detail.prompt_templates?.[0]?.prompts ?? [];
      const random = promptList[Math.floor(Math.random() * promptList.length)] ?? '';
      setActivePrompt(random);
      setView('active');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Не удалось загрузить дрилл.');
    }
  }

  async function submit() {
    if (!active || !response.trim()) return;
    try {
      const session = await apiClient.post<{ session_id: string }>(
        '/api/v1/coach/perform',
        { skill: active.skill, prompt: activePrompt },
      );

      // Parallel: rating + speech-analysis (if audio recorded).
      const ratingPromise = apiClient.post<{
        ai_evaluation?: string;
        feedback?: string[];
      }>(`/api/v1/coach/perform/${session.session_id}/submit`, {
        response: response.trim(),
      });

      const audioBlob = lastBlobRef.current;
      const speechPromise: Promise<SpeechAnalysisResponse | null> = audioBlob
        ? (async () => {
            const fd = new FormData();
            fd.append(
              'file',
              new File([audioBlob.blob], `coach.${audioBlob.ext}`, {
                type: audioBlob.blob.type,
              }),
            );
            try {
              return await apiClient.post<SpeechAnalysisResponse>(
                '/api/v1/voice/speech-analysis',
                fd,
              );
            } catch {
              return null;
            }
          })()
        : Promise.resolve(null);

      const [result, speech] = await Promise.all([ratingPromise, speechPromise]);
      const text =
        result.ai_evaluation ??
        (Array.isArray(result.feedback) ? result.feedback.join('\n\n') : '') ??
        'Сохранено.';
      setSubmitted(text);
      setSpeechMetrics(speech);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Не удалось сохранить дрилл.');
    }
  }

  if (view === 'active' && active) {
    return (
      <PageStage>
        <SectionHead
          eyebrow={`Дрилл · ${active.skill}`}
          title={active.name}
          italic="—"
          sub={active.description}
        />
        <div className={styles.prompt}>{activePrompt || 'Сформулируйте свой вариант.'}</div>
        <textarea
          className={styles.area}
          rows={8}
          value={response}
          onChange={(e) => setResponse(e.currentTarget.value)}
          placeholder="Запишите ваш ответ. Можно сырой, без полировки."
        />
        <CoachVoiceRow
          onAppend={(t) => setResponse((prev) => (prev ? `${prev} ${t}` : t))}
          onBlobReady={(blob, ext) => {
            lastBlobRef.current = { blob, ext };
          }}
        />
        <div className={styles.actions}>
          <button className={styles.linkBtn} onClick={() => setView('picker')}>
            ← к списку
          </button>
          <Button onClick={submit} disabled={!response.trim()} trailing="→">
            оценить
          </Button>
        </div>
        {submitted && (
          <div className={styles.evaluation}>
            <div className={styles.evalLabel}>Разбор</div>
            <p>{submitted}</p>
          </div>
        )}
        {speechMetrics && <SpeechMetricsCard metrics={speechMetrics} />}
      </PageStage>
    );
  }

  return (
    <PageStage>
      <SectionHead
        eyebrow="Тренер"
        title="Дрилл"
        italic="по навыкам"
        sub="Короткие упражнения. По одному за раз. Прицельно по слабостям."
      />
      <Link to="/coach/speech" className={styles.speechCard}>
        <div className={styles.speechHead}>
          <span className={styles.speechTitle}>Разбор речи</span>
          <span className={styles.speechCta}>● ЗАПИСАТЬ →</span>
        </div>
        <p className={styles.speechSub}>
          Whisper транскрибирует, ИИ считает темп, паразиты, затупы и паузы —
          потом разбирает где провисаешь.
        </p>
      </Link>
      {loading ? (
        <p className={styles.muted}>загружаем…</p>
      ) : categories.length === 0 ? (
        <p className={styles.muted}>каталог пока пуст.</p>
      ) : (
        <ul className={styles.list}>
          {categories.map((c) => (
            <li key={c.skill}>
              <button
                onClick={() => pick(c)}
                className={styles.row}
                disabled={!c.populated}
                style={!c.populated ? { opacity: 0.4 } : undefined}
              >
                <span className={styles.rowSkill}>{skillLabel(c.skill)}</span>
                <span className={styles.rowDiff}>
                  {c.populated
                    ? `сложность ${c.difficulty_range[0]}–${c.difficulty_range[1]}/5`
                    : 'v1.5'}
                </span>
                <span className={styles.rowArrow}>{c.populated ? '→' : ''}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </PageStage>
  );
}

function skillLabel(skill: string): string {
  const map: Record<string, string> = {
    impromptu: 'Импровизация',
    opener_library: 'Опенеры',
    recognition_through_memory: 'Узнавание по памяти',
    specific_observation: 'Точное наблюдение',
    empathy_to_question: 'Эмпатия → вопрос',
    discomfort_zone: 'Зона дискомфорта',
    cross_domain_explanation: 'Объяснение через другую сферу',
    english_rescue: 'Английский — спасение разговора',
    niche_vocabulary: 'Нишевый словарь',
    realistic_cost_of_error: 'Реалистичная цена ошибки',
    three_things_i_bring: 'Три вещи, которые я приношу',
    comprehension_audit: 'Audit понимания (англ.)',
  };
  return map[skill] ?? skill;
}

function CoachVoiceRow({
  onAppend,
  onBlobReady,
}: {
  onAppend: (text: string) => void;
  onBlobReady?: (blob: Blob, ext: string) => void;
}) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  function pickMime(): string | null {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];
    for (const c of candidates) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) return c;
    }
    return null;
  }

  function mimeToExt(mime: string): string {
    if (mime.includes('webm')) return 'webm';
    if (mime.includes('ogg')) return 'ogg';
    if (mime.includes('mp4')) return 'm4a';
    return 'webm';
  }

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        recorderRef.current = null;
        void transcribe(blob, rec.mimeType || 'audio/webm');
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error((e as Error).message || 'Браузер не дал доступ к микрофону.');
    }
  }

  function stop() {
    setRecording(false);
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
  }

  async function transcribe(blob: Blob, mime: string) {
    setTranscribing(true);
    try {
      const ext = mimeToExt(mime);
      const file = new File([blob], `coach.${ext}`, { type: mime });
      const fd = new FormData();
      fd.append('file', file);
      const r = await apiClient.post<TranscribeResponse>('/api/v1/voice/transcribe', fd);
      const text = (r.text || '').trim();
      if (text) {
        onAppend(text);
        onBlobReady?.(blob, ext);
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Whisper не справился');
    } finally {
      setTranscribing(false);
    }
  }

  return (
    <div className={styles.voiceRow}>
      <button
        type="button"
        className={recording ? styles.voiceBtnRec : styles.voiceBtn}
        onClick={recording ? stop : start}
        disabled={transcribing}
        aria-label={recording ? 'Остановить запись' : 'Записать голосом'}
      >
        {recording ? '■' : '●'}
      </button>
      <div className={styles.voiceMeta}>
        {transcribing
          ? 'Whisper расшифровывает…'
          : recording
            ? 'Запись · жми квадрат когда закончил'
            : 'Ответить голосом — добавится в текст'}
      </div>
    </div>
  );
}

function SpeechMetricsCard({ metrics }: { metrics: SpeechAnalysisResponse }) {
  const tempo =
    metrics.wpm < 1
      ? ''
      : metrics.wpm < 110
        ? '(вяло)'
        : metrics.wpm < 130
          ? '(спокойно)'
          : metrics.wpm <= 160
            ? '(норма)'
            : metrics.wpm <= 185
              ? '(быстро)'
              : '(тараторишь)';
  return (
    <div className={styles.metricsCard}>
      <div className={styles.metricsLabel}>Речь · метрики</div>
      <MetricRow label="Темп" value={`${Math.round(metrics.wpm)} wpm ${tempo}`} />
      <MetricRow
        label="Паразиты"
        value={`${metrics.filler_total} (${metrics.filler_pct}% речи)`}
      />
      <MetricRow label="Затупы > 0.8 с" value={`${metrics.hesitations_count}`} />
      <MetricRow
        label="Предложения"
        value={`ср. ${metrics.avg_sentence_length} · макс ${metrics.max_sentence_length}`}
      />
      {metrics.fillers.length > 0 && (
        <div className={styles.fillersInline}>
          {metrics.fillers
            .slice(0, 5)
            .map((f) => `«${f.word}»×${f.count}`)
            .join(' · ')}
        </div>
      )}
      {metrics.commentary && <p className={styles.metricsCommentary}>{metrics.commentary}</p>}
    </div>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.metricRow}>
      <span className={styles.metricLabel}>{label}</span>
      <span className={styles.metricValue}>{value}</span>
    </div>
  );
}
